import React, { useEffect, useState } from "react";

export interface HtmlViewerProps {
  /**
   * HTML markup, or a `blob:` / `http(s):` URL whose body is fetched and shown.
   * Rendered inside a sandboxed iframe (`srcDoc`); scripts do not run.
   */
  source: string;
  fileName?: string;
}

function isFetchableUrl(s: string): boolean {
  return (
    s.startsWith("blob:") ||
    s.startsWith("http://") ||
    s.startsWith("https://")
  );
}

/**
 * Empty sandbox = maximum restrictions (no scripts, no form submit to parent, etc.).
 */
const IFRAME_SANDBOX = "";

/** Iframes are replaced elements; flex `flex-1` often yields 0 height without an explicit floor. */
const VIEWPORT_STYLE: React.CSSProperties = {
  minHeight: "min(560px, 70vh)",
  width: "100%",
};

const IFRAME_STYLE: React.CSSProperties = {
  display: "block",
  width: "100%",
  minHeight: "min(560px, 70vh)",
  height: "100%",
  border: 0,
  backgroundColor: "#fff",
};

const HtmlViewer: React.FC<HtmlViewerProps> = ({ source, fileName }) => {
  const [html, setHtml] = useState<string>(() =>
    source && !isFetchableUrl(source) ? source : "",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    if (!source) {
      setHtml("");
      return;
    }

    if (!isFetchableUrl(source)) {
      setHtml(source);
      return;
    }

    setHtml("");

    (async () => {
      try {
        const isHttp =
          source.startsWith("http://") || source.startsWith("https://");
        const res = await fetch(source, {
          ...(isHttp
            ? {
                credentials: "include" as RequestCredentials,
                mode: "cors" as RequestMode,
              }
            : {}),
        });
        if (!res.ok) {
          throw new Error(`Failed to load file (${res.status})`);
        }
        const body = await res.text();
        if (!cancelled) setHtml(body);
      } catch (e) {
        if (!cancelled) {
          setHtml("");
          setError(
            e instanceof Error ? e.message : "Could not read HTML file.",
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source]);

  if (error) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100"
        style={VIEWPORT_STYLE}
      >
        {error}
      </div>
    );
  }

  const waitingForFetch = isFetchableUrl(source) && html === "";

  if (waitingForFetch) {
    return (
      <div
        className="flex flex-1 flex-col items-center justify-center text-hex-gray-500 bg-white border-t border-hex-gray-100"
        style={VIEWPORT_STYLE}
      >
        <span className="text-sm">Loading preview…</span>
      </div>
    );
  }

  return (
    <div
      className="flex flex-1 flex-col bg-white border-t border-hex-gray-100"
      style={VIEWPORT_STYLE}
    >
      <iframe
        key={`${fileName ?? "html"}-${html.length}`}
        title={fileName ? `HTML preview: ${fileName}` : "HTML preview"}
        srcDoc={html}
        sandbox={IFRAME_SANDBOX}
        style={IFRAME_STYLE}
      />
    </div>
  );
};

export default HtmlViewer;
